import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import io from 'socket.io-client';
import './App.css';

// Déclaration des constantes globales
const primaryColor = '#0055A4'; // Bleu FFVL
const secondaryColor = '#EAEAEA'; // Fond gris clair
const licenseRegex = /^[A-Z]{2}\d{6}$/;

const countries = ["France", "Belgique", "Suisse", "Canada", "États-Unis", "Royaume-Uni", "Allemagne", "Espagne", "Italie"];
const languages = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" }
];

const translations = {
  fr: {
    welcome: "Bienvenue sur Fly Safe Dune",
    selectLanguage: "Pour une expérience optimale, sélectionnez votre langue et autorisez notifications et audio.",
    startDemo: "Démarrer la démo",
    inscription: "Inscription",
    modeChoice: "Je suis :",
    pilotSolo: "Pilote seul",
    pilotGroup: "Pilote en groupe",
    firstName: "Prénom",
    lastName: "Nom",
    license: "Numéro de licence FFVL",
    nationality: "Nationalité",
    email: "Email",
    phone: "Téléphone",
    saveNext: "Enregistrer & Suivant",
    registered: "Vous êtes enregistré sur parapilat.com",
    validate: "Valider et Continuer",
    insuranceAgreement: "Je m'engage à être détenteur d'une assurance en responsabilité civile aérienne.",
    tutorialTitle: "Tutoriel Sécurité (10s)",
    passVideo: "Passer la vidéo",
    weatherBulletin: "☁️ Bulletin Météo : Vitesse du vent :",
    windWarning: "⚠️ Vents violents, prudence maximale !",
    windAcceptable: "🙂 Conditions acceptables.",
    livePilots: "Nombre de pilotes présents aujourd'hui",
    flightButton: "Mise en vol",
    sosButton: "SOS",
    admin: "Admin",
    globalSOS: "Déclencher secours global (admin)",
    clearSOS: "Lever l'alerte (admin)",
    return: "Retour"
  },
  en: {
    welcome: "Welcome to Fly Safe Dune",
    selectLanguage: "For optimal experience, select your language and allow notifications and audio.",
    startDemo: "Start Demo",
    inscription: "Registration",
    modeChoice: "I am:",
    pilotSolo: "Solo Pilot",
    pilotGroup: "Group Pilot",
    firstName: "First Name",
    lastName: "Last Name",
    license: "FFVL License Number",
    nationality: "Nationality",
    email: "Email",
    phone: "Phone",
    saveNext: "Save & Next",
    registered: "You are registered on parapilat.com",
    validate: "Validate and Continue",
    insuranceAgreement: "I agree to hold valid aerial liability insurance.",
    tutorialTitle: "Safety Tutorial (10s)",
    passVideo: "Skip Video",
    weatherBulletin: "☁️ Weather Bulletin: Wind Speed:",
    windWarning: "⚠️ Strong winds, exercise extreme caution!",
    windAcceptable: "🙂 Acceptable conditions.",
    livePilots: "Number of pilots present today",
    flightButton: "Take Off",
    sosButton: "SOS",
    admin: "Admin",
    globalSOS: "Trigger global SOS (admin)",
    clearSOS: "Clear alert (admin)",
    return: "Back"
  }
};

// Correction de l'icône par défaut de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Icône pour le parapente
const paragliderIcon = new L.Icon({
  iconUrl: process.env.PUBLIC_URL + '/paraglider_icon.png',
  iconSize: [35, 35],
  iconAnchor: [17, 35],
  popupAnchor: [0, -35],
});

// Style pour les zones d'exclusion
const exclusionStyle = {
  color: 'red',
  weight: 2,
};

// Fonction d'info-bulle pour les zones d'exclusion
const handleEachExclusionFeature = (feature, layer) => {
  if (feature.properties && feature.properties.name) {
    layer.bindPopup(`${feature.properties.icon || ''} ${feature.properties.name}`);
  }
};

// Fonction d'aide : détection de point dans un polygone (ray-casting)
function pointInPolygon(point, polygon) {
  let x = point[1], y = point[0];
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    let xi = polygon[i][1], yi = polygon[i][0];
    let xj = polygon[j][1], yj = polygon[j][0];
    let intersect = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function App() {
  // États d'internationalisation
  const [selectedLanguage, setSelectedLanguage] = useState("fr");
  const t = translations[selectedLanguage];

  // Initialisation du socket via useRef
  const socketRef = useRef(null);
  useEffect(() => {
    // Connectez-vous au serveur Socket.IO (remplacez l'URL si nécessaire)
    socketRef.current = io("http://localhost:4000");
    socketRef.current.on("sos", () => {
      // Lorsqu'un événement SOS est reçu, déclenchez l'alarme sur ce client
      triggerSOS();
    });
    return () => socketRef.current.disconnect();
  }, []);

  // États pour démarrer la démo et gérer l'étape (0 = écran de démarrage)
  const [demoStarted, setDemoStarted] = useState(false);
  const [step, setStep] = useState(0);

  // États pour l'inscription
  const [mode, setMode] = useState('solo');
  const [groupCount, setGroupCount] = useState(1);
  const [pilots, setPilots] = useState([]);
  const [currentPilot, setCurrentPilot] = useState({
    firstName: 'Jean',
    lastName: 'Dupont',
    license: 'AB123456',
    nationality: 'France',
    email: 'jean.dupont@example.com',
    phone: '0123456789'
  });
  const [pilotIndex, setPilotIndex] = useState(0);
  const [insuranceAgreement, setInsuranceAgreement] = useState(false);

  // Tutoriel vidéo simulé
  const tutorialDuration = 10000;
  const [videoFinished, setVideoFinished] = useState(false);

  // Bulletin météo et vent simulé
  const [weatherBulletin, setWeatherBulletin] = useState("");
  const [windSpeed, setWindSpeed] = useState(20);

  // Position initiale de l'utilisateur
  const [userPosition, setUserPosition] = useState([44.601608023627215, -1.20987416951217]);

  // États pour le mode admin et SOS
  const [adminMode, setAdminMode] = useState(false);
  const [sosTriggered, setSosTriggered] = useState(false);
  const [globalSOS, setGlobalSOS] = useState(false);
  const audioRef = useRef(null);

  // Charger les zones d'exclusion depuis exclusionZones.json
  const [exclusionZones, setExclusionZones] = useState(null);
  useEffect(() => {
    fetch(process.env.PUBLIC_URL + '/exclusionZones.json')
      .then(response => response.json())
      .then(data => setExclusionZones(data))
      .catch(error => console.error("Erreur lors du chargement des zones d'exclusion :", error));
  }, []);

  // Zone d'exclusion temporaire (admin)
  const [tempZone, setTempZone] = useState(null);
  const addTempExclusionZone = () => {
    const newZone = {
      type: "Feature",
      properties: { name: "Zone Temporaire - Nidification", icon: "🐣" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [-1.208, 44.582],
          [-1.204, 44.582],
          [-1.204, 44.584],
          [-1.208, 44.584],
          [-1.208, 44.582]
        ]]
      }
    };
    setTempZone(newZone);
  };
  const removeTempExclusionZone = () => setTempZone(null);

  // Suppression d'un pilote
  const handleDeletePilot = (index) => {
    setPilots(pilots.filter((_, i) => i !== index));
  };

  // Validation du formulaire d'inscription
  const isCurrentPilotValid = () => {
    return currentPilot.firstName.trim() !== '' &&
           currentPilot.lastName.trim() !== '' &&
           licenseRegex.test(currentPilot.license.trim().toUpperCase()) &&
           currentPilot.nationality.trim() !== '' &&
           currentPilot.email.trim() !== '' &&
           currentPilot.phone.trim() !== '';
  };

  const handleSavePilot = () => {
    if (!isCurrentPilotValid()) {
      alert(`Veuillez remplir correctement tous les champs. (${t.license} doit respecter le format, ex: AB123456)`);
      return;
    }
    setPilots([...pilots, { ...currentPilot, license: currentPilot.license.trim().toUpperCase() }]);
    setCurrentPilot({
      firstName: 'Jean',
      lastName: 'Dupont',
      license: 'AB123456',
      nationality: 'France',
      email: 'jean.dupont@example.com',
      phone: '0123456789'
    });
    if (pilotIndex + 1 < groupCount) {
      setPilotIndex(pilotIndex + 1);
    } else {
      setStep(3);
    }
  };

  // Fonction de géolocalisation réelle (optionnelle)
  const requestGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => setUserPosition([position.coords.latitude, position.coords.longitude]),
        error => console.error("Erreur de géolocalisation :", error)
      );
    } else {
      alert("La géolocalisation n'est pas supportée par votre navigateur.");
    }
  };

  // Préparation de l'audio
  const prepareAudio = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(process.env.PUBLIC_URL + '/alert.mp3');
      audioRef.current.play().then(() => {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }).catch(error => console.error("Erreur lors de la préparation audio :", error));
    }
  };

  // Autorisation des notifications
  useEffect(() => {
    if (demoStarted && Notification.permission !== "granted") {
      Notification.requestPermission().then(permission => console.log("Permission notifications :", permission));
    }
  }, [demoStarted]);

  // Mise à jour du bulletin météo
  useEffect(() => {
    const updateWeather = () => {
      const newWindSpeed = Math.floor(Math.random() * 41) + 10;
      setWindSpeed(newWindSpeed);
      let bulletin = `${t.weatherBulletin} ${newWindSpeed} km/h.`;
      bulletin += newWindSpeed > 35 ? ` ${t.windWarning}` : ` ${t.windAcceptable}`;
      setWeatherBulletin(bulletin);
    };
    updateWeather();
    const weatherTimer = setInterval(updateWeather, 15000);
    return () => clearInterval(weatherTimer);
  }, [t]);

  // Compteur live simulé
  const [simulatedCount, setSimulatedCount] = useState(pilots.length);
  useEffect(() => {
    setSimulatedCount(pilots.length);
  }, [pilots]);
  useEffect(() => {
    const interval = setInterval(() => {
      setSimulatedCount(prevCount => {
        const change = Math.random() > 0.5 ? 1 : -1;
        let newCount = prevCount + change;
        return newCount < 0 ? 0 : newCount;
      });
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Simulation du vol
  const [isFlying, setIsFlying] = useState(false);
  const [currentPathIndex, setCurrentPathIndex] = useState(0);
  const flightPath = [
    [44.601608023627215, -1.20987416951217], // Départ (Camping Pila)
    [44.602, -1.208],
    [44.603, -1.207],
    [44.604, -1.206], // Zone d'exclusion
    [44.605, -1.205]  // Au-dessus du Restaurant La Corniche
  ];
  const exclusionZonePolygon = [
    [44.6045, -1.2065],
    [44.6045, -1.2045],
    [44.606, -1.2045],
    [44.606, -1.2065]
  ];
  const startFlight = () => {
    setIsFlying(true);
    setCurrentPathIndex(0);
  };
  useEffect(() => {
    if (isFlying && currentPathIndex < flightPath.length) {
      const timer = setInterval(() => {
        const newPos = flightPath[currentPathIndex];
        setUserPosition(newPos);
        console.log("Coordonnées simulées :", newPos);
        if (pointInPolygon(newPos, exclusionZonePolygon)) {
          if (!sosTriggered) {
            triggerSOS();
          }
        } else {
          if (sosTriggered) {
            stopSOS();
          }
        }
        setCurrentPathIndex(prev => prev + 1);
      }, 3000);
      return () => clearInterval(timer);
    }
  }, [isFlying, currentPathIndex, sosTriggered, flightPath, exclusionZonePolygon]);

  // Fonctions SOS
  const triggerSOS = () => {
    let countdown = 5;
    const interval = setInterval(() => {
      if (countdown > 1) {
        alert(`SOS déclenché dans ${countdown - 1} seconde(s) si non annulé.`);
        countdown--;
      } else {
        clearInterval(interval);
        setSosTriggered(true);
        if (Notification.permission !== "granted") {
          Notification.requestPermission();
        }
        if (Notification.permission === "granted") {
          new Notification("ALERTE SOS", { body: "Vous devez atterrir maintenant !" });
        }
        if (navigator.vibrate) {
          navigator.vibrate([200, 100, 200]);
        }
        if (audioRef.current) {
          audioRef.current.play().catch(error => console.error("Erreur de lecture audio :", error));
        }
      }
    }, 1000);
  };

  const stopSOS = () => {
    setSosTriggered(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  // Fonctions admin pour SOS global
  const adminTriggerGlobalSOS = () => {
    // Émettre l'événement SOS via le socket pour diffuser à tous les clients
    if (socketRef.current) {
      socketRef.current.emit("sos");
    }
    // Optionnellement, déclencher localement l'alarme
    triggerSOS();
  };
  const adminClearSOS = () => {
    setGlobalSOS(false);
    stopSOS();
  };

  const completeRegistration = () => {
    if (pilots.length === 0) {
      alert("Veuillez enregistrer au moins un pilote.");
      return;
    }
    if (!insuranceAgreement) {
      alert(t.insuranceAgreement);
      return;
    }
    setStep(4);
  };

  // Tutoriel vidéo simulé
  useEffect(() => {
    if (step === 4) {
      const timer = setTimeout(() => {
        setVideoFinished(true);
      }, tutorialDuration);
      return () => clearTimeout(timer);
    }
  }, [step]);
  useEffect(() => {
    if (videoFinished && step === 4) {
      setStep(5);
      requestGeolocation();
    }
  }, [videoFinished, step]);

  // Rendu du bulletin météo
  const renderWeatherBulletin = () => (
    <div style={styles.weatherBulletin}>
      <p>{weatherBulletin}</p>
    </div>
  );

  // Bouton "Retour"
  const renderBackButton = () => (
    <button style={styles.button} onClick={() => setStep(prev => (prev > 1 ? prev - 1 : prev))}>
      {t.return}
    </button>
  );

  // Rendu de l'en-tête
  const renderHeader = () => (
    <header style={styles.header}>
      <img src={process.env.PUBLIC_URL + '/ffvl_logo.png'} alt="Logo FFVL" style={styles.logo} />
      <h1 style={styles.h1}>Fly Safe Dune</h1>
      {adminMode && <span style={styles.adminBadge}>{t.admin}</span>}
    </header>
  );

  // Barre de progression
  const renderProgressBar = () => {
    if (step === 1 || step === 2) {
      const progress = step === 1 ? 50 : ((pilotIndex + 1) / groupCount) * 100;
      return (
        <div style={styles.progressBarContainer}>
          <div style={{ ...styles.progressBar, width: `${progress}%` }} />
        </div>
      );
    }
    return null;
  };

  // Rendu du contenu en fonction de l'étape
  const renderContent = () => {
    switch (step) {
      case 0: return renderStartScreen();
      case 1: return renderModeSelection();
      case 2: return renderPilotForm();
      case 3: return renderValidation();
      case 4: return renderTutorialVideo();
      case 5: return renderMainInterface();
      default: return null;
    }
  };

  // Écran de démarrage avec sélection de langue (étape 0)
  const renderStartScreen = () => (
    <div style={styles.startScreen}>
      <h1>{t.welcome}</h1>
      <p>{t.selectLanguage}</p>
      <select
        value={selectedLanguage}
        onChange={e => setSelectedLanguage(e.target.value)}
        style={styles.input}
      >
        {languages.map(lang => (
          <option key={lang.code} value={lang.code}>
            {lang.flag} {lang.label}
          </option>
        ))}
      </select>
      <button style={styles.button} onClick={() => { 
        prepareAudio(); 
        setDemoStarted(true); 
        setStep(1); 
      }}>
        {t.startDemo}
      </button>
    </div>
  );

  // Étape 1 : Choix du mode d'inscription
  const renderModeSelection = () => (
    <div style={styles.formStep}>
      <h2>{t.inscription}</h2>
      <p>{t.modeChoice}</p>
      <button style={styles.iconButton} onClick={() => { setMode('solo'); setGroupCount(1); setStep(2); }}>
        👤 {t.pilotSolo}
      </button>
      <button style={styles.iconButton} onClick={() => { setMode('groupe'); setGroupCount(0); setStep(2); }}>
        👥 {t.pilotGroup}
      </button>
    </div>
  );

  // Étape 2 : Formulaire d'inscription pour chaque pilote
  const renderPilotForm = () => (
    <div style={styles.formStep}>
      <h2>{t.inscription} - Pilote {mode === 'groupe' ? pilotIndex + 1 : ''}</h2>
      <label>{t.firstName} :</label>
      <input
        type="text"
        value={currentPilot.firstName}
        onChange={e => setCurrentPilot({ ...currentPilot, firstName: e.target.value })}
        style={styles.input}
      />
      <label>{t.lastName} :</label>
      <input
        type="text"
        value={currentPilot.lastName}
        onChange={e => setCurrentPilot({ ...currentPilot, lastName: e.target.value })}
        style={styles.input}
      />
      <label>{t.license} :</label>
      <input
        type="text"
        placeholder="Ex: AB123456"
        value={currentPilot.license}
        onChange={e => setCurrentPilot({ ...currentPilot, license: e.target.value })}
        style={styles.input}
      />
      <label>{t.nationality} :</label>
      <select
        value={currentPilot.nationality}
        onChange={e => setCurrentPilot({ ...currentPilot, nationality: e.target.value })}
        style={styles.input}
      >
        <option value="">{t.nationality} - Sélectionnez un pays</option>
        {countries.map(country => (
          <option key={country} value={country}>{country}</option>
        ))}
      </select>
      <label>{t.email} :</label>
      <input
        type="email"
        value={currentPilot.email}
        onChange={e => setCurrentPilot({ ...currentPilot, email: e.target.value })}
        style={styles.input}
      />
      <label>{t.phone} :</label>
      <input
        type="tel"
        value={currentPilot.phone}
        onChange={e => setCurrentPilot({ ...currentPilot, phone: e.target.value })}
        style={styles.input}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {renderBackButton()}
        <button style={styles.button} onClick={handleSavePilot}>{t.saveNext}</button>
      </div>
    </div>
  );

  // Étape 3 : Validation finale de l'inscription
  const renderValidation = () => (
    <div style={styles.formStep}>
      <h2>{t.inscription} - Validation</h2>
      <p>{`Nombre de pilotes enregistrés : ${pilots.length} / ${groupCount}`}</p>
      <div style={styles.registeredInfo}>
        {pilots.map((pilot, index) => (
          <div key={index} style={styles.pilotInfo}>
            <span style={styles.validIcon}>✔️</span> {pilot.firstName} {pilot.lastName} – {t.license}: {pilot.license}
            <button style={styles.deleteButton} onClick={() => handleDeletePilot(index)}>✖️</button>
            <br /><em>{t.registered}</em>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <label style={{ marginBottom: '10px' }}>
          <input
            type="checkbox"
            checked={insuranceAgreement}
            onChange={e => setInsuranceAgreement(e.target.checked)}
          />{' '}
          {t.insuranceAgreement}
        </label>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {renderBackButton()}
          <button style={styles.button} onClick={completeRegistration}>{t.validate}</button>
        </div>
      </div>
    </div>
  );

  // Étape 4 : Tutoriel vidéo simulé
  const renderTutorialVideo = () => (
    <div style={styles.tutorialContainer}>
      <h2>{t.tutorialTitle}</h2>
      <p>La vidéo se lance automatiquement. Vous pouvez cliquer pour passer.</p>
      <div style={styles.fakeVideo}>
        <p style={{ color: '#fff', textAlign: 'center' }}>[ Vidéo de démonstration ]</p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {renderBackButton()}
        <button style={{ ...styles.button, marginTop: 20 }} onClick={() => setVideoFinished(true)}>
          {t.passVideo}
        </button>
      </div>
    </div>
  );

  // Étape 5 : Interface principale
  const renderMainInterface = () => (
    <div style={styles.mainPage}>
      <h2>{t.welcome}</h2>
      <p><strong>{t.livePilots} :</strong> {simulatedCount}</p>
      {renderWeatherBulletin()}
      <p>Informations des pilotes :</p>
      {pilots.map((pilot, index) => (
        <div key={index} style={styles.pilotInfo}>
          <span style={styles.validIcon}>✔️</span> {pilot.firstName} {pilot.lastName} – {t.license}: {pilot.license}
        </div>
      ))}
      <div style={styles.mapContainer}>
        <MapContainer center={[44.582, -1.203]} zoom={15} style={{ height: '300px', width: '100%' }}>
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={[44.582, -1.203]}>
            <Popup>Dune du Pilat</Popup>
          </Marker>
          {userPosition && (
            <Marker position={userPosition} icon={isFlying ? paragliderIcon : new L.Icon.Default()}>
              <Popup>{isFlying ? "Vous volez en parapente" : "Vous êtes ici"}</Popup>
            </Marker>
          )}
          {exclusionZones && (
            <GeoJSON data={exclusionZones} style={exclusionStyle} onEachFeature={handleEachExclusionFeature} />
          )}
          {tempZone && (
            <GeoJSON data={tempZone} style={exclusionStyle} onEachFeature={handleEachExclusionFeature} />
          )}
        </MapContainer>
      </div>
      <div style={styles.mainButtons}>
        <button style={{ ...styles.button, backgroundColor: '#3BA145' }} onClick={startFlight}>
          {t.flightButton}
        </button>
        <button style={{ ...styles.button, backgroundColor: '#BF1313' }} onClick={triggerSOS}>
          {t.sosButton}
        </button>
      </div>
      {adminMode && (
        <div style={styles.adminControls}>
          <button style={styles.button} onClick={addTempExclusionZone}>
            Ajouter Zone Temporaire
          </button>
          <button style={styles.button} onClick={removeTempExclusionZone}>
            Supprimer Zone Temporaire
          </button>
          <button style={styles.button} onClick={adminTriggerGlobalSOS}>
            {t.globalSOS}
          </button>
          <button style={styles.button} onClick={adminClearSOS}>
            {t.clearSOS}
          </button>
        </div>
      )}
    </div>
  );

  // Rendu final de l'application
  return (
    <div style={styles.container}>
      {renderHeader()}
      {(step === 1 || step === 2) && renderProgressBar()}
      <div style={styles.content}>{renderContent()}</div>
      <button style={styles.adminToggle} onClick={() => setAdminMode(!adminMode)}>
        {adminMode ? 'Quitter le mode Admin' : 'Passer en mode Admin'}
      </button>
    </div>
  );
}

const styles = {
  container: {
    fontFamily: 'Arial, sans-serif',
    backgroundColor: secondaryColor,
    minHeight: '100vh',
    padding: '10px',
    color: '#333'
  },
  header: {
    backgroundColor: primaryColor,
    padding: '10px',
    textAlign: 'center',
    position: 'relative'
  },
  logo: {
    width: '40px',
    position: 'absolute',
    left: '10px',
    top: '10px'
  },
  h1: {
    color: '#fff',
    margin: 0,
    fontSize: '1.5rem'
  },
  adminBadge: {
    position: 'absolute',
    right: '10px',
    top: '10px',
    backgroundColor: 'red',
    color: '#fff',
    padding: '5px',
    borderRadius: '5px',
    fontSize: '0.8rem'
  },
  startScreen: {
    textAlign: 'center',
    padding: '40px'
  },
  content: {
    maxWidth: '500px',
    margin: '20px auto',
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 0 4px rgba(0,0,0,0.1)'
  },
  progressBarContainer: {
    height: '8px',
    backgroundColor: '#ccc',
    borderRadius: '4px',
    overflow: 'hidden',
    margin: '10px auto',
    maxWidth: '500px'
  },
  progressBar: {
    height: '8px',
    backgroundColor: primaryColor,
    transition: 'width 0.3s ease'
  },
  formStep: {
    display: 'flex',
    flexDirection: 'column'
  },
  input: {
    margin: '5px 0 15px 0',
    padding: '8px',
    fontSize: '1rem',
    borderRadius: '4px',
    border: '1px solid #ccc'
  },
  button: {
    backgroundColor: primaryColor,
    color: '#fff',
    border: 'none',
    padding: '10px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    marginTop: '10px'
  },
  iconButton: {
    backgroundColor: primaryColor,
    color: '#fff',
    border: 'none',
    padding: '10px 15px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    margin: '10px'
  },
  deleteButton: {
    background: 'none',
    border: 'none',
    color: 'red',
    fontSize: '1rem',
    position: 'absolute',
    right: '5px',
    top: '5px',
    cursor: 'pointer'
  },
  tutorialContainer: {
    textAlign: 'center'
  },
  fakeVideo: {
    width: '100%',
    height: '200px',
    backgroundColor: '#666',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px'
  },
  mainPage: {
    textAlign: 'center'
  },
  weatherBulletin: {
    backgroundColor: '#F8F8F8',
    padding: '10px',
    borderRadius: '4px',
    margin: '10px 0',
    fontSize: '1rem'
  },
  mapContainer: {
    margin: '20px 0'
  },
  mainButtons: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: '15px'
  },
  pilotInfo: {
    backgroundColor: secondaryColor,
    padding: '5px',
    margin: '5px 0',
    borderRadius: '4px',
    position: 'relative'
  },
  validIcon: {
    marginRight: '5px',
    color: 'green'
  },
  adminToggle: {
    backgroundColor: '#444',
    color: '#fff',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    display: 'block',
    margin: '10px auto'
  },
  adminControls: {
    marginTop: '15px'
  },
  registeredInfo: {
    marginBottom: '15px'
  }
};

export default App;
